/**
 * Geometry for the Clarity chart — C50 and C80 overlaid vs. frequency. D50
 * isn't plotted alongside them (it's a percentage, not dB — a shared axis
 * with C50/C80 would be meaningless); callers report its average in the
 * chart's caption instead, same as other charts' peak/summary captions.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, type LinearScale, type LogFreqScale } from './scales';
import { mapSeriesToPlot, yDomainFromSeries, type PlotPoint } from './series';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import type { ClarityFits } from './clarity';

export type ClarityMetric = 'c50' | 'c80';
export const CLARITY_METRICS: ClarityMetric[] = ['c50', 'c80'];

export interface ClaritySeries {
  metric: ClarityMetric;
  points: PlotPoint[];
}

export interface ClarityChartInput {
  fits: ClarityFits;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface ClarityChartGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  series: ClaritySeries[];
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeClarityChartGeometry(opts: ClarityChartInput): ClarityChartGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 200;
  const fMax = opts.fMax ?? 20000;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const { bandHz } = opts.fits;
  if (!bandHz.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, -10, 10, true),
      series: [], xTicks, yTicks: [],
    };
  }

  const c50Finite = opts.fits.c50Db.filter(Number.isFinite);
  const c80Finite = opts.fits.c80Db.filter(Number.isFinite);
  const yDomain = (c50Finite.length || c80Finite.length)
    ? yDomainFromSeries([{ x: bandHz, y: opts.fits.c50Db }, { x: bandHz, y: opts.fits.c80Db }])
    : { lo: -10, hi: 10 };
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const series: ClaritySeries[] = CLARITY_METRICS.map((metric) => ({
    metric,
    points: mapSeriesToPlot(
      { x: bandHz, y: metric === 'c50' ? opts.fits.c50Db : opts.fits.c80Db },
      xScale, yScale,
    ),
  }));

  const step = Math.max(1, Math.round((yDomain.hi - yDomain.lo) / 6));
  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: v === 0, label: `${v.toFixed(0)}`,
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, series, xTicks, yTicks };
}
